import { useCallback } from "react";
import CsvImporter from "@/components/CsvImporter";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listSitesLite, upsertPicklistItem } from "@/lib/api";

/**
 * /admin/import/picklists — bulk-load tenant picklist values into
 * lbs_picklists. Uses upsertPicklistItem (insert, no update path here
 * because there is no stable composite-id from CSV alone — re-running
 * with the same values is harmless because picklist_key + value is
 * effectively unique within a client/scope set).
 */

const KEYS = [
  "vehicle_type",
  "goods_category",
  "ppe",
  "pallet_type",
  "load_rule",
  "restraint_method",
  "temp_set",
  "issue_type",
  "cold_chain",
];

const COLUMNS = [
  { key: "picklist_key",       label: "Picklist key", required: true,
    description: `One of: ${KEYS.join(", ")}.`, example: "vehicle_type" },
  { key: "value",              label: "Value",        required: true,
    description: "Stable machine value (lowercase, snake_case).",
    example: "b_double" },
  { key: "label",              label: "Label",        required: true,
    description: "Human-readable label shown in dropdowns.",
    example: "B-Double" },
  { key: "sort_order",         label: "Sort order",
    description: "Integer. Lower = earlier in the dropdown. Defaults to 0.",
    example: "10" },
  { key: "scope_site_codes",   label: "Site scope",
    description: "Comma-separated site codes; blank = all sites in the tenant.",
    example: "MEL01,SYD02" },
];

const KEYS_SET = new Set(KEYS);

export default function AdminImportPicklists() {
  const { assignments, hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();

  const targetClientId =
    isSuperAdmin ? (viewAsClientId || null)
                 : (assignments?.[0]?.client_id || null);

  const loadContext = useCallback(async () => {
    if (!targetClientId) {
      throw new Error(isSuperAdmin
        ? "Super admin: please pick a client in the top bar first."
        : "Your account has no tenant assignment.");
    }
    const { data: sites } = await listSitesLite(targetClientId);
    const sitesByCode = new Map();
    (sites || []).forEach((s) => { if (s.code) sitesByCode.set(s.code.toLowerCase(), s); });
    return { clientId: targetClientId, sitesByCode };
  }, [targetClientId, isSuperAdmin]);

  const validateRow = (raw, ctx) => {
    const errors = [];
    let level = "valid";

    const key   = (raw.picklist_key || "").trim();
    const value = (raw.value || "").trim();
    const label = (raw.label || "").trim();
    const sortRaw = (raw.sort_order || "").trim();
    const sites   = (raw.scope_site_codes || "").trim();

    if (!key)   errors.push("picklist_key required");
    else if (!KEYS_SET.has(key)) errors.push(`unknown picklist_key "${key}"`);
    if (!value) errors.push("value required");
    if (!label) errors.push("label required");

    let sortOrder = 0;
    if (sortRaw) {
      const n = parseInt(sortRaw, 10);
      if (Number.isNaN(n)) errors.push("sort_order must be integer");
      else sortOrder = n;
    }

    let scopeSiteIds = null;
    if (sites) {
      const codes = sites.split(",").map((c) => c.trim()).filter(Boolean);
      const ids = [];
      const missing = [];
      codes.forEach((c) => {
        const s = ctx.sitesByCode.get(c.toLowerCase());
        if (s) ids.push(s.id);
        else missing.push(c);
      });
      if (missing.length) { errors.push(`unknown site code(s): ${missing.join(", ")}`); level = "warn"; }
      scopeSiteIds = ids.length ? ids : null;
    }

    if (errors.some((e) =>
      e === "picklist_key required" || e.startsWith("unknown picklist_key")
      || e === "value required" || e === "label required"
      || e === "sort_order must be integer"
    )) {
      level = "error";
    }

    return {
      level,
      errors,
      normalised: {
        client_id: ctx.clientId,
        picklist_key: key,
        value,
        label,
        sort_order: sortOrder,
        scope_site_ids: scopeSiteIds,
        is_active: true,
      },
    };
  };

  const commitRow = async (n) => {
    const res = await upsertPicklistItem(n);
    if (res?.error) return { ok: false, error: res.error.message };
    return { ok: true };
  };

  if (!hasRole("super_admin") && !hasRole("client_admin")) {
    return <p className="text-sm text-rose-700">Admins only.</p>;
  }

  return (
    <div data-testid="admin-import-picklists-page">
      {!targetClientId && (
        <div className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3" data-testid="picklists-import-no-tenant">
          {isSuperAdmin
            ? "Pick a client from the top-bar 'View as client' picker before importing."
            : "No tenant assignment found on your account."}
        </div>
      )}
      <CsvImporter
        title="Import picklists"
        testid="picklists-import"
        columns={COLUMNS}
        loadContext={loadContext}
        validateRow={validateRow}
        commitRow={commitRow}
      />
    </div>
  );
}
